import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { DATE_TIME_FORMAT, QUERY_NAME, SERVICES } from "@lhd/constants";
import { handleMessageError } from "@lhd/features/Apps/utils";
import axios from "@lhd/libs/axios";
import { getCompanyId, getUid } from "@lhd/utility";
import { notification } from "antd";
import moment from "moment";
import {
  Appraisal,
  AppraisalSetting,
  AppraisalSheet,
  AppraisalSheetDetail,
  AppraisalSheetDetailFeedback,
} from "types/appraisal.type";

const normalizeAppraisalSheetId = (id: string | number | null | undefined) =>
  id == null ? id : String(id);

export const useFetchAppraisals = (
  page: number,
  pageSize: number,
  filterParams: any,
) => {
  const companyId = getCompanyId();
  const uid = getUid();
  const { __BLOCK_API__, ...restFilterParams } = filterParams || {};

  return useQuery(
    [QUERY_NAME.APPRAISALS, companyId, page, pageSize, filterParams, uid],
    () =>
      axios({
        method: "get",
        url: SERVICES.APPRAISALS,
        params: {
          page,
          per_page: pageSize,
          company_id: companyId,
          ...restFilterParams,
        },
      }).then((res) => ({
        data: res.data as Appraisal[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
    {
      cacheTime: 3000,
      staleTime: 3000,
      enabled: !__BLOCK_API__,
    },
  );
};

export const useExportAppraisals = (
  page: number,
  pageSize: number,
  filterParams: any,
  typeExport: "excel" | "csv",
) => {
  const companyId = getCompanyId();

  return useQuery(
    [],
    async () => {
      const response = await axios({
        method: "get",
        url: SERVICES.APPRAISALS,
        params: {
          page,
          per_page: pageSize,
          company_id: companyId,
          ...filterParams,
          export_type: typeExport,
        },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        "目標_" +
          moment().format(DATE_TIME_FORMAT.DATE_JP_TIME) +
          (typeExport === "csv" ? ".csv" : ".xlsx"),
      );
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      return response;
    },
    {
      cacheTime: 3000,
      staleTime: 3000,
      enabled: false, // Disable automatic fetching
    },
  );
};

export const useFetchOneAppraisal = (appraisalId: any) => {
  const uid = getUid();
  const companyId = getCompanyId();
  return useQuery(
    [QUERY_NAME.APPRAISAL, appraisalId, uid, companyId],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.APPRAISALS}/${appraisalId}`,
        params: {
          company_id: companyId,
        },
      }).then((res) => res.data as Appraisal),
    {
      cacheTime: 3000,
      staleTime: 3000,
      retry: 2,
      onError({ response }: any) {
        notification["error"]({
          message: response?.data?.error,
        });
      },
      enabled: !!appraisalId,
    },
  );
};

export const useFetchAppraisalKgiList = (
  page: number,
  pageSize: number,
  filterParams: any,
) => {
  const companyId = getCompanyId();
  const uid = getUid();
  const { __BLOCK_API__, ...restFilterParams } = filterParams || {};
  return useQuery(
    [
      `${QUERY_NAME.APPRAISALS}_kgi_list`,
      companyId,
      page,
      pageSize,
      filterParams,
      uid,
    ],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.APPRAISALS}/worker/list_kgi`,
        params: {
          page,
          per_page: pageSize,
          company_id: companyId,
          ...restFilterParams,
        },
      }).then((res) => ({
        data: res.data as any,
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
    {
      cacheTime: 1000,
      staleTime: 1000,
      enabled: !__BLOCK_API__,
    },
  );
};

export const useExportAppraisalKgiList = (
  page: number,
  pageSize: number,
  filterParams: any,
  typeExport: "excel" | "csv",
) => {
  const companyId = getCompanyId();

  return useQuery(
    [],
    async () => {
      const response = await axios({
        method: "get",
        url: `${SERVICES.APPRAISALS}/worker/list_kgi`,
        params: {
          page,
          per_page: pageSize,
          company_id: companyId,
          ...filterParams,
          export_type: typeExport,
        },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        "行動目標一覧_" +
          moment().format(DATE_TIME_FORMAT.DATE_JP_TIME) +
          (typeExport === "csv" ? ".csv" : ".xlsx"),
      );
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      return response;
    },
    {
      cacheTime: 3000,
      staleTime: 3000,
      enabled: false, // Disable automatic fetching
    },
  );
};

export const useDeleteMultipleAppraisal = () => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    ({ listIds }: any) => {
      const formData = new FormData();
      listIds.forEach((id: any) => {
        formData.append(`delete_ids[]`, id);
      });
      formData.append("company_id", companyId);
      return axios({
        method: "post",
        data: formData,
        url: `${SERVICES.APPRAISALS}/delete_multiple`,
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.APPRAISALS]);
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

export const useSubmitAppraisal = () => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();

  return useMutation(
    ({ payload, appraisalId }: any) =>
      axios({
        method: appraisalId ? "patch" : "post",
        url: appraisalId
          ? `${SERVICES.APPRAISALS}/${appraisalId}`
          : SERVICES.APPRAISALS,
        data: { ...payload, company_id: companyId },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.APPRAISALS]);
        queryClient.invalidateQueries([QUERY_NAME.APPRAISAL]);
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

export const usePublishAppraisal = () => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    ({ appraisalId }: any) =>
      axios({
        method: "post",
        url: `${SERVICES.APPRAISALS}/${appraisalId}/publish`,
        params: {
          company_id: companyId,
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.APPRAISAL]);
        queryClient.invalidateQueries([QUERY_NAME.APPRAISALS]);
        // notification["success"]({
        //   message: "保存しました",
        // });
      },
    },
  );
};

export const usePublishAdditionAppraisals = () => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    ({ appraisalId, appraisal_member_ids }: any) =>
      axios({
        method: "post",
        url: `${SERVICES.APPRAISALS}/${appraisalId}/publish/addition`,
        data: { appraisal_member_ids },
        params: {
          company_id: companyId,
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.APPRAISAL]);
        queryClient.invalidateQueries([QUERY_NAME.APPRAISALS]);
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

export const useUnPublishAppraisal = () => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    ({ appraisalId }: any) =>
      axios({
        method: "post",
        url: `${SERVICES.APPRAISALS}/${appraisalId}/quit_publishing`,
        params: { company_id: companyId },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.APPRAISALS]);
        queryClient.invalidateQueries([QUERY_NAME.APPRAISAL]);
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

export const useCloneAppraisal = () => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    ({ appraisalId }: any) =>
      axios({
        method: "post",
        url: `${SERVICES.APPRAISALS}/${appraisalId}/clone`,
        params: {
          company_id: companyId,
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.APPRAISALS]);
        queryClient.invalidateQueries([QUERY_NAME.APPRAISAL]);
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

export const useReissueAppraisalSheet = () => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();

  return useMutation(
    ({ appraisal_sheet_ids }: any) =>
      axios({
        method: "post",
        url: `${SERVICES.APPRAISAL_SHEETS}/reissue_bulk`,
        data: { appraisal_sheet_ids },
        params: {
          company_id: companyId,
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.APPRAISALS]);
        queryClient.invalidateQueries([QUERY_NAME.APPRAISAL]);
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

// Appraisal setting
export const useFetchAppraisalSettings = (
  page: number,
  pageSize: number,
  filterParams: any,
) => {
  const companyId = getCompanyId();
  const uid = getUid();
  return useQuery(
    [
      QUERY_NAME.APPRAISAL_SETTINGS,
      companyId,
      page,
      pageSize,
      filterParams,
      uid,
    ],
    () =>
      axios({
        method: "get",
        url: SERVICES.APPRAISAL_SETTINGS,
        params: {
          page,
          per_page: pageSize,
          ...filterParams,
        },
      }).then((res) => ({
        data: res.data as AppraisalSetting[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
    {
      retry: 2,
      cacheTime: 3000,
      staleTime: 3000,
    },
  );
};

export const useFetchOneAppraisalSetting = (appraisalSettingId: any) => {
  const uid = getUid();
  return useQuery(
    [QUERY_NAME.APPRAISAL_SETTING, appraisalSettingId, uid],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.APPRAISAL_SETTINGS}/${appraisalSettingId}`,
      }).then((res) => res.data as AppraisalSetting),
    {
      cacheTime: 3000,
      staleTime: 3000,
      retry: 2,
      enabled: !!appraisalSettingId,
    },
  );
};

export const useFetchAppraisalSettingCurrent = () => {
  const uid = getUid();
  const companyId = getCompanyId();
  return useQuery(
    [`${QUERY_NAME.APPRAISAL_SETTING}_current-detail`, companyId, uid],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.APPRAISAL_SETTINGS}/current-detail`,
        params: {
          company_id: companyId,
        },
      }).then((res) => res.data as AppraisalSetting),
    {
      retry: 2,
    },
  );
};

export const useDeleteAppraisalSetting = (appraisalSettingId: string) => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    () =>
      axios({
        method: "delete",
        url: `${SERVICES.APPRAISAL_SETTINGS}/${appraisalSettingId}`,
        params: {
          company_id: companyId,
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.APPRAISAL_SETTINGS]);
        queryClient.invalidateQueries([
          `${QUERY_NAME.APPRAISAL_SETTING}_current-detail`,
        ]);
        notification["info"]({
          message: "削除が完了しました",
        });
      },
    },
  );
};

export const useSubmitAppraisalSetting = () => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();

  return useMutation(
    ({ payload, appraisalSettingId }: any) =>
      axios({
        method: appraisalSettingId ? "put" : "post",
        url: appraisalSettingId
          ? `${SERVICES.APPRAISAL_SETTINGS}/${appraisalSettingId}`
          : SERVICES.APPRAISAL_SETTINGS,
        data: { ...payload, company_id: companyId },
        params: {
          company_id: companyId,
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.APPRAISAL_SETTING]);
        queryClient.invalidateQueries([QUERY_NAME.APPRAISAL_SETTINGS]);
        queryClient.invalidateQueries([
          `${QUERY_NAME.APPRAISAL_SETTING}_current-detail`,
        ]);
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

// Appraisal sheets
export const useFetchAppraisalSheets = (
  page: number,
  pageSize: number,
  filterParams: any,
) => {
  const uid = getUid();
  const companyId = getCompanyId();
  let statusParams: any;

  const { __BLOCK_API__, ...restFilterParams } = filterParams || {};

  if (restFilterParams?.status) {
    statusParams = restFilterParams?.status?.split(",");
  }

  return useQuery(
    [QUERY_NAME.APPRAISAL_SHEETS, companyId, page, pageSize, filterParams, uid],
    () =>
      axios({
        method: "get",
        url: SERVICES.APPRAISAL_SHEETS,
        params: {
          page,
          per_page: pageSize,
          company_id: companyId,
          ...restFilterParams,
          ...(statusParams ? { status: statusParams } : {}),
        },
      }).then((res) => ({
        data: res.data as AppraisalSheet[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
    {
      cacheTime: 3000,
      staleTime: 3000,
      enabled: !__BLOCK_API__,
    },
  );
};

export const useExportAppraisalSheets = (
  page: number,
  pageSize: number,
  filterParams: any,
  typeExport: "excel" | "csv",
  nameExport = "評価シート",
) => {
  const companyId = getCompanyId();
  let statusParams: any;

  if (filterParams?.status) {
    statusParams = filterParams?.status?.split(",");
  }

  return useQuery(
    [],
    async () => {
      const response = await axios({
        method: "get",
        url: SERVICES.APPRAISAL_SHEETS,
        params: {
          page,
          per_page: pageSize,
          company_id: companyId,
          ...filterParams,
          ...(statusParams ? { status: statusParams } : {}),
          export_type: typeExport,
        },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        nameExport +
          "_" +
          moment().format(DATE_TIME_FORMAT.DATE_JP_TIME) +
          (typeExport === "csv" ? ".csv" : ".xlsx"),
      );
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      return response;
    },
    {
      cacheTime: 3000,
      staleTime: 3000,
      enabled: false,
    },
  );
};

export const useFetchAppraisalSheetsDetails = (id?: string) => {
  const uid = getUid();
  const companyId = getCompanyId();
  const sheetId = normalizeAppraisalSheetId(id);

  return useQuery(
    [QUERY_NAME.APPRAISAL_SHEETS_DETAILS, companyId, uid, sheetId],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.APPRAISAL_SHEETS}/${sheetId}/details`,
      }).then((res) => ({
        data: res.data as AppraisalSheetDetail[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
    {
      cacheTime: 3000,
      staleTime: 3000,
    },
  );
};

export const useFetchAppraisalSheetsUnseen = (
  page: number,
  pageSize: number,
  filterParams: any,
) => {
  const uid = getUid();
  const companyId = getCompanyId();

  return useQuery(
    [
      QUERY_NAME.APPRAISAL_SHEETS_UNSEEN,
      companyId,
      page,
      pageSize,
      filterParams,
      uid,
    ],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.APPRAISAL_SHEETS}/unseen`,
        params: {
          page,
          per_page: pageSize,
          company_id: companyId,
          ...filterParams,
        },
      }).then((res) => ({
        data: res.data as AppraisalSheet[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
    {
      cacheTime: 3000,
      staleTime: 3000,
    },
  );
};

export const useFetchOneAppraisalSheets = (appraisalSheetId: any) => {
  const uid = getUid();
  const companyId = getCompanyId();
  const sheetId = normalizeAppraisalSheetId(appraisalSheetId);
  return useQuery(
    [QUERY_NAME.APPRAISAL_SHEET, companyId, sheetId, uid],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.APPRAISAL_SHEETS}/${sheetId}`,
        params: {
          company_id: companyId,
        },
      }).then((res) => res.data as AppraisalSheet),
    {
      cacheTime: 3000,
      staleTime: 3000,
      retry: 0,
      onError({ response }: any) {
        // notification["error"]({
        //   message: response?.data?.error,
        // });
      },
      enabled: !!appraisalSheetId,
    },
  );
};

export const useSubmitAppraisalSheet = () => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  const uid = getUid();

  return useMutation(
    ({ payload, appraisalSheetId }: any) =>
      axios({
        method: "put",
        url: `${SERVICES.APPRAISAL_SHEETS}/${appraisalSheetId}`,
        data: { ...payload, company_id: companyId },
        params: {
          company_id: companyId,
        },
      }),
    {
      async onSuccess(response, variables) {
        const sheetId = normalizeAppraisalSheetId(variables.appraisalSheetId);
        const sheetKey = [QUERY_NAME.APPRAISAL_SHEET, companyId, sheetId, uid];
        const detailsKey = [
          QUERY_NAME.APPRAISAL_SHEETS_DETAILS,
          companyId,
          uid,
          sheetId,
        ];

        await queryClient.cancelQueries(sheetKey);
        await queryClient.cancelQueries(detailsKey);
        queryClient.setQueryData(sheetKey, response.data as AppraisalSheet);
        queryClient.setQueryData(detailsKey, {
          data: response.data?.appraisal_sheet_details || [],
          meta: { total: 0, total_page: 0 },
        });
        queryClient.invalidateQueries([QUERY_NAME.APPRAISAL_SHEETS]);
      },
    },
  );
};

export const useSeenAppraisalSheet = () => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();

  return useMutation(
    ({ appraisalSheetId }: any) =>
      axios({
        method: "patch",
        url: `${SERVICES.APPRAISAL_SHEETS}/${appraisalSheetId}/mark-seen`,
        params: {
          company_id: companyId,
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.APPRAISAL_SHEETS_UNSEEN]);
        queryClient.invalidateQueries([QUERY_NAME.APPRAISAL_SHEETS]);
        queryClient.invalidateQueries([QUERY_NAME.APPRAISAL_SHEET]);
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

export const useDeleteMultipleAppraisalSheets = () => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();

  return useMutation(
    ({ listIds }: any) => {
      const formData = new FormData();
      listIds.forEach((id: any) => {
        formData.append(`appraisal_sheet_ids[]`, id);
      });
      formData.append(`company_id`, companyId);
      return axios({
        method: "delete",
        data: formData,
        url: `${SERVICES.APPRAISAL_SHEETS}/bulk`,
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.APPRAISAL_SHEETS]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError({ response }: any) {
        notification["error"]({
          message: handleMessageError(response?.data?.error),
        });
      },
    },
  );
};

// handle submit/approve/reject sheet
export const useChangeStatusOneAppraisalSheet = () => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();

  return useMutation(
    ({
      appraisalSheetId,
      status,
      comment,
    }: {
      appraisalSheetId: number;
      status: "approve" | "reject" | "submit" | "unsubmit";
      comment?: string;
    }) =>
      axios({
        method: "post",
        url: `${SERVICES.APPRAISAL_SHEETS}/${appraisalSheetId}/${status}`,
        data: {
          company_id: companyId,
          comment,
        },
        params: {
          company_id: companyId,
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.APPRAISAL_SHEETS]);
        queryClient.invalidateQueries([QUERY_NAME.APPRAISAL_SHEETS_DETAILS]);
        queryClient.invalidateQueries([QUERY_NAME.APPRAISAL_SHEET]);
      },
    },
  );
};

export const useAdminChangeStatusOneAppraisalSheet = () => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();

  return useMutation(
    ({
      appraisalSheetId,
      status,
      comment,
    }: {
      appraisalSheetId: number;
      status: "approve" | "reject" | "submit" | "unsubmit";
      comment?: string;
    }) =>
      axios({
        method: "post",
        url: `${SERVICES.APPRAISAL_SHEETS}/${appraisalSheetId}/admin/${status}`,
        data: {
          company_id: companyId,
          comment,
        },
        params: {
          company_id: companyId,
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.APPRAISAL_SHEETS]);
        queryClient.invalidateQueries([QUERY_NAME.APPRAISAL_SHEETS_DETAILS]);
        queryClient.invalidateQueries([QUERY_NAME.APPRAISAL_SHEET]);
      },
    },
  );
};

export const useFetchAppraisalSheetsDetailFeedbacks = (
  appraisalSheetDetailId?: string,
  receiverWorkerId?: number | "all" | "appraiser",
) => {
  const uid = getUid();

  return useQuery(
    [
      QUERY_NAME.APPRAISAL_SHEETS_DETAIL_FEEDBACKS,
      uid,
      appraisalSheetDetailId,
      receiverWorkerId,
    ],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.APPRAISAL_SHEET_DETAILS}/${appraisalSheetDetailId}/feedbacks`,
        params:
          typeof receiverWorkerId === "number"
            ? {
                relation_worker_id: receiverWorkerId,
              }
            : receiverWorkerId === "all"
            ? { group: "all" }
            : { group: "appraiser" },
      }).then((res) => ({
        data: res.data as AppraisalSheetDetailFeedback[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
    {
      cacheTime: 3000,
      staleTime: 3000,
      enabled: !!appraisalSheetDetailId,
      onError({ response }: any) {
        notification["error"]({
          message: response?.data?.error,
        });
      },
    },
  );
};

export const useSubmitAppraisalSheetFeedbacks = () => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();

  return useMutation(
    ({ payload, appraisalSheetDetailId }: any) =>
      axios({
        method: "post",
        url: `${SERVICES.APPRAISAL_SHEET_DETAILS}/${appraisalSheetDetailId}/feedbacks`,
        data: { ...payload, company_id: companyId },
        params: {
          company_id: companyId,
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([
          QUERY_NAME.APPRAISAL_SHEETS_DETAIL_FEEDBACKS,
        ]);
        queryClient.invalidateQueries([QUERY_NAME.APPRAISAL_SHEETS_DETAILS]);
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

export const useDeleteOneAppraisalSheetFeedback = () => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    ({ appraisalSheetDetailId, feedbackId }: any) =>
      axios({
        method: "delete",
        url: `${SERVICES.APPRAISAL_SHEET_DETAILS}/${appraisalSheetDetailId}/feedbacks/${feedbackId}`,
        params: {
          company_id: companyId,
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([
          QUERY_NAME.APPRAISAL_SHEETS_DETAIL_FEEDBACKS,
        ]);
        queryClient.invalidateQueries([QUERY_NAME.APPRAISAL_SHEETS_DETAILS]);
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

export const useBulkChangeStatusAppraisalSheet = () => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();
  return useMutation(
    ({ status, appraisalSheetIds }: any) =>
      axios({
        method: "post",
        url: `${SERVICES.APPRAISAL_SHEETS}/admin/change/bulk`,
        data: {
          appraisal_sheet_status: status,
          appraisal_sheet_ids: appraisalSheetIds,
        },
        params: {
          company_id: companyId,
        },
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([
          QUERY_NAME.APPRAISAL_SHEETS_DETAIL_FEEDBACKS,
        ]);
        queryClient.invalidateQueries([QUERY_NAME.APPRAISAL_SHEETS]);
      },
    },
  );
};

export const useFetchAverageLastAppraiserMetrics = (
  appraisal_ids?: number[],
) => {
  const companyId = getCompanyId();
  const uid = getUid();
  return useQuery(
    [
      `${SERVICES.APPRAISAL_SHEETS}/reports/average_last_appraiser_metrics`,
      companyId,
      uid,
      appraisal_ids,
    ],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.APPRAISAL_SHEETS}/reports/average_last_appraiser_metrics`,
        params: {
          company_id: companyId,
          appraisal_ids,
        },
      }).then((res) => ({
        data: res.data as {
          average_gap: number;
          average_kgi: number;
          average_kpi: number;
          average_point: number;
        }[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
    {
      cacheTime: 3000,
      staleTime: 3000,
    },
  );
};

export interface AppraisalFilterOptionItem {
  value: string;
  count: number;
  label?: string;
}

export interface AppraisalFilterOptionsParams {
  company_id?: string | number;
  field_key?: string;
  active_filters?: Record<string, string[]>;
  filter_order?: string[];
}

export const useFetchAppraisalFilterOptions = (
  params: AppraisalFilterOptionsParams | null,
  enabled?: boolean,
) => {
  const companyId = getCompanyId();
  return useQuery(
    [
      QUERY_NAME.APPRAISALS,
      "filter_options",
      params?.company_id || companyId,
      params?.field_key,
      params?.active_filters,
      params?.filter_order,
    ],
    async () => {
      if (!params) return null;

      const res = await axios({
        method: "post",
        url: `${SERVICES.APPRAISALS}/filter_options`,
        data: {
          company_id: params.company_id || companyId,
          field_key: params.field_key,
          active_filters: params.active_filters,
          filter_order: params.filter_order,
        },
      });
      return res.data;
    },
    {
      enabled: enabled && !!params?.field_key,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    },
  );
};

export interface AppraisalSheetFilterOptionsParams {
  company_id?: string | number;
  appraisal_id?: string | number | null;
  appraisal_ids?: Array<string | number>;
  worker_id?: string | number | null;
  field_key?: string;
  active_filters?: Record<string, string[]>;
  filter_order?: string[];
}

export const useFetchAppraisalSheetFilterOptions = (
  params: AppraisalSheetFilterOptionsParams | null,
  enabled?: boolean,
) => {
  const companyId = getCompanyId();
  return useQuery(
    [
      QUERY_NAME.APPRAISAL_SHEETS,
      "filter_options",
      params?.company_id || companyId,
      params?.appraisal_id,
      params?.field_key,
      params?.active_filters,
      params?.filter_order,
    ],
    async () => {
      if (!params) return null;

      const res = await axios({
        method: "post",
        url: `${SERVICES.APPRAISAL_SHEETS}/filter_options`,
        data: {
          company_id: params.company_id || companyId,
          appraisal_id: params.appraisal_id,
          appraisal_ids: params.appraisal_ids,
          worker_id: params.worker_id,
          field_key: params.field_key,
          active_filters: params.active_filters,
          filter_order: params.filter_order,
        },
      });
      return res.data;
    },
    {
      enabled: enabled && !!params?.field_key,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    },
  );
};

export interface AppraisalKgiFilterOptionsParams {
  company_id?: string | number;
  appraisal_ids?: Array<string | number>;
  worker_ids?: Array<string | number>;
  field_key?: string;
  active_filters?: Record<string, string[]>;
  filter_order?: string[];
}

export const useFetchAppraisalKgiFilterOptions = (
  params: AppraisalKgiFilterOptionsParams | null,
  enabled?: boolean,
) => {
  const companyId = getCompanyId();

  return useQuery(
    [
      `${QUERY_NAME.APPRAISALS}_kgi_list`,
      "filter_options",
      params?.company_id || companyId,
      params?.appraisal_ids,
      params?.worker_ids,
      params?.field_key,
      params?.active_filters,
      params?.filter_order,
    ],
    async () => {
      if (!params) return null;

      const res = await axios({
        method: "post",
        url: `${SERVICES.APPRAISALS}/worker/list_kgi/filter_options`,
        data: {
          company_id: params.company_id || companyId,
          appraisal_ids: params.appraisal_ids,
          worker_ids: params.worker_ids,
          field_key: params.field_key,
          active_filters: params.active_filters,
          filter_order: params.filter_order,
        },
      });
      return res.data;
    },
    {
      enabled: enabled && !!params?.field_key,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    },
  );
};
